import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

const MIN_THUMB_PX = 24;
const SCROLL_IDLE_MS = 700;

/**
 * Native scrolling viewport with a custom hover-visible scrollbar thumb.
 * Wheel, touch, keyboard, and programmatic scroll remain browser-native.
 */
@Component({
  selector: 'bds-scroll-region',
  standalone: true,
  imports: [NgClass],
  templateUrl: './scroll-region.component.html',
  styleUrl: './scroll-region.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScrollRegionComponent implements AfterViewInit, OnDestroy {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _ngZone = inject(NgZone);

  @ViewChild('viewport', { static: true })
  private readonly _viewportRef!: ElementRef<HTMLElement>;

  @ViewChild('content', { static: true })
  private readonly _contentRef!: ElementRef<HTMLElement>;

  /** Extra classes applied to the native scroll viewport. */
  @Input() styleClass = '';

  /** Optional fixed height (e.g. `calc(100vh - 170px)`). */
  @Input() height?: string;

  /**
   * Optional maximum height for content-sized overlays (e.g. `60vh`). The region
   * grows with its content up to this cap, then scrolls. Ignored when `height` is set.
   */
  @Input() maxHeight?: string;

  /** Accessible name for the scrollable region. */
  @Input() ariaLabel = 'Scrollable content';

  readonly overflow = signal(false);
  readonly thumbHeightPx = signal(MIN_THUMB_PX);
  readonly thumbTopPx = signal(0);
  readonly dragging = signal(false);
  readonly scrolling = signal(false);

  @HostBinding('style.height')
  get hostHeight(): string | null {
    return this.height ?? null;
  }

  /** In max-height mode the host grows to content (capped by `maxHeight`) instead of filling its parent. */
  @HostBinding('class.bds-scroll-region--max-height')
  get isMaxHeightMode(): boolean {
    return !this.height && !!this.maxHeight;
  }

  @HostBinding('style.max-height')
  get hostMaxHeight(): string | null {
    return this.isMaxHeightMode ? (this.maxHeight ?? null) : null;
  }

  private _resizeObserver: ResizeObserver | null = null;
  private _contentObserver: ResizeObserver | null = null;
  private _scrollIdleTimer: ReturnType<typeof setTimeout> | null = null;
  private _dragStartY = 0;
  private _dragStartScrollTop = 0;

  get viewportElement(): HTMLElement {
    return this._viewportRef.nativeElement;
  }

  ngAfterViewInit(): void {
    const viewport = this.viewportElement;

    fromEvent(viewport, 'scroll', { passive: true })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._markScrolling();
        this._updateThumbMetrics();
      });

    if (typeof ResizeObserver !== 'undefined') {
      const onResize = () =>
        this._ngZone.run(() => this._updateThumbMetrics());
      this._resizeObserver = new ResizeObserver(onResize);
      this._resizeObserver.observe(viewport);

      // Observe aggregate projected content as rows/messages are added or removed.
      this._contentObserver = new ResizeObserver(onResize);
      this._contentObserver.observe(this._contentRef.nativeElement);
    }

    queueMicrotask(() => this._updateThumbMetrics());
  }

  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._contentObserver?.disconnect();
    if (this._scrollIdleTimer) {
      clearTimeout(this._scrollIdleTimer);
    }
  }

  scrollTo(options: ScrollToOptions): void {
    this.viewportElement.scrollTo(options);
  }

  onThumbPointerDown(event: PointerEvent): void {
    if (!this.overflow()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const thumb = event.currentTarget as HTMLElement;
    try {
      thumb.setPointerCapture(event.pointerId);
    } catch {
      // jsdom / older browsers may not support pointer capture
    }
    this.dragging.set(true);
    this._dragStartY = event.clientY;
    this._dragStartScrollTop = this.viewportElement.scrollTop;
  }

  onThumbPointerMove(event: PointerEvent): void {
    if (!this.dragging()) {
      return;
    }
    const viewport = this.viewportElement;
    const maxScroll = viewport.scrollHeight - viewport.clientHeight;
    if (maxScroll <= 0) {
      return;
    }
    const trackTravel = viewport.clientHeight - this.thumbHeightPx();
    if (trackTravel <= 0) {
      return;
    }
    const deltaY = event.clientY - this._dragStartY;
    const scrollDelta = (deltaY / trackTravel) * maxScroll;
    viewport.scrollTop = this._dragStartScrollTop + scrollDelta;
  }

  onThumbPointerUp(event: PointerEvent): void {
    if (!this.dragging()) {
      return;
    }
    const thumb = event.currentTarget as HTMLElement;
    try {
      if (thumb.hasPointerCapture?.(event.pointerId)) {
        thumb.releasePointerCapture(event.pointerId);
      }
    } catch {
      // ignore
    }
    this.dragging.set(false);
  }

  /** Exposed for unit tests. */
  updateThumbMetricsForTest(): void {
    this._updateThumbMetrics();
  }

  private _markScrolling(): void {
    this.scrolling.set(true);
    if (this._scrollIdleTimer) {
      clearTimeout(this._scrollIdleTimer);
    }
    this._scrollIdleTimer = setTimeout(() => {
      this.scrolling.set(false);
      this._scrollIdleTimer = null;
    }, SCROLL_IDLE_MS);
  }

  private _updateThumbMetrics(): void {
    const viewport = this.viewportElement;
    const { clientHeight, scrollHeight, scrollTop } = viewport;
    const canScroll = scrollHeight > clientHeight + 1;
    this.overflow.set(canScroll);

    if (!canScroll) {
      this.thumbHeightPx.set(MIN_THUMB_PX);
      this.thumbTopPx.set(0);
      return;
    }

    const ratio = clientHeight / scrollHeight;
    const thumbHeight = Math.max(MIN_THUMB_PX, Math.round(clientHeight * ratio));
    const maxScroll = scrollHeight - clientHeight;
    const maxThumbTop = clientHeight - thumbHeight;
    const thumbTop =
      maxScroll > 0
        ? Math.round((scrollTop / maxScroll) * maxThumbTop)
        : 0;

    this.thumbHeightPx.set(thumbHeight);
    this.thumbTopPx.set(thumbTop);
  }
}
